<script>
import debounce from 'lodash/debounce';
import Draggable from 'vuedraggable';
import Selectable from '@/components/selectable/Selectable';
import FileListItem from '@/components/file-list/FileListItem';

export default {
  name: 'FileList',
  components: {
    Draggable,
    FileListItem,
    Selectable
  },
  props: {
    allowDelete: Boolean,
    allowDrop: Boolean,
    allowSort: Boolean,
    canSelect: {
      type: Function,
      default: () => true
    },
    emptyText: String,
    errorText: String,
    filterMethod: {
      type: Function,
      default: () => false
    },
    hasError: Boolean,
    height: {
      type: Number,
      default: 320
    },
    loading: Boolean,
    multiple: Boolean,
    value: {
      type: Array,
      required: true
    }
  },
  data() {
    return {
      isDragging: false,
      items: [],
      selection: []
    };
  },
  watch: {
    value(val) {
      this.items = val;
    }
  },
  created() {
    this.items = this.value;
  },
  methods: {
    getSelection() {
      return this.selection;
    },

    setSelection(items) {
      items = Array.isArray(items) ? items : [items];
      items = items.filter(item => item && this.canSelect(item));
      if (!this.multiple) items = items.slice(0, 1);
      this.$refs.selectable.setSelection(items);
    },

    deleteSelection() {
      if (this.allowDelete && this.selection.length) {
        this.items = this.items.filter(item => !this.selection.includes(item));
        this.$emit('input', this.items);
      }
    },

    handleDragOver(event) {
      if (this.allowDrop) {
        event.preventDefault();
        this.isDragging = true;
      }
    },

    handleDragLeave(event) {
      if (this.allowDrop) {
        event.preventDefault();
        this.isDragging = false;
      }
    },

    handleDrop(event) {
      if (this.allowDrop) {
        event.preventDefault();
        this.isDragging = false;
        this.$emit('drop', event);
      }
    },

    handleKeyDown(event) {
      if (['Backspace', 'Delete'].includes(event.key)) {
        this.deleteSelection();
      }
    },

    handleScroll: debounce(
      function () {
        const list = this.$refs.list;
        const listHeight = list.offsetHeight;
        const scrollTop = list.scrollTop;
        const scrollHeight = list.scrollHeight;
        const nearThreshold = listHeight / 2;

        if (scrollTop + listHeight + nearThreshold >= scrollHeight) {
          this.$emit('scroll-near-bottom');
        }

        if (scrollTop + listHeight >= scrollHeight) {
          this.$emit('scroll-at-bottom');
        }
      },
      500,
      { maxWait: 100 }
    ),

    handleSelect(item, event) {
      if (this.canSelect(item)) {
        this.$refs.selectable.select(item, event);
      } else {
        this.$refs.selectable.selectNone();
      }
    },

    scrollToTop() {
      const list = this.$refs.list;
      list.scrollTop = 0;
    },

    scrollToSelection() {
      const list = this.$refs.list;
      const min = list.scrollTop;
      const max = list.scrollTop + list.offsetHeight;
      const item = list.querySelector('.is-selected'); // first selected item

      // Only scroll if the selected item is out of view
      if (item && (item.offsetTop < min || item.offsetTop > max)) {
        list.scrollTop = item.offsetTop - list.offsetHeight / 2 + item.offsetHeight / 2;
      }
    }
  }
};
</script>

<template>
  <div
    v-loading="loading"
    :class="{
      'file-list-container': true,
      'is-dragging': isDragging
    }"
    @dragover.prevent="handleDragOver"
    @dragleave.prevent="handleDragLeave"
    @drop.prevent="handleDrop"
  >
    <div ref="list" :style="`height: ${height}px;`" class="file-list" @scroll="handleScroll">
      <selectable
        v-if="!hasError"
        ref="selectable"
        v-model="selection"
        :items="items"
        :multiple="multiple"
        key-prop="path"
        @input="$emit('select', selection)"
        @keydown.native="handleKeyDown"
        @click.native="$refs.selectable.selectNone"
      >
        <draggable
          v-model="items"
          :disabled="!allowSort || items.length === 0"
          :fallback-tolerance="2"
          :force-fallback="true"
          :scroll-sensitivity="45"
          :scroll-speed="15"
          :animation="250"
          @sort="$emit('input', items)"
          @update="$emit('sort', items)"
        >
          <!-- Files -->
          <file-list-item
            v-for="item in items"
            :key="JSON.stringify(item)"
            :busy="item.isBusy"
            :disabled="item.isDisabled"
            :enabled="item.isEnabled"
            :hidden="filterMethod(item)"
            :name="item.name"
            :path="item.path"
            :selected="$refs.selectable.isSelected(item)"
            :size="item.size"
            :type="item.type"
            @click.stop="
              $emit('click', item);
              handleSelect(item, $event);
            "
            @dblclick="$emit('dblclick', item)"
            @path-change="$emit('path-change', $event)"
          />
        </draggable>
      </selectable>

      <!-- Error state -->
      <div v-if="hasError" class="error-message">
        {{ errorText || $t('Error') }}
      </div>

      <!-- Empty state -->
      <div v-else-if="items.length === 0 && emptyText" class="no-items">
        {{ emptyText }}
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import '@/styles/variables.scss';

.file-list-container {
  border: solid 1px $--color-gray-20;
  border-radius: 0.25rem;
  background: $--color-white;
  font-size: 1rem;
  line-height: 1.4;
  user-select: none;
  margin-bottom: 1rem;
  transition: 0.1s border, 0.1s background-color;

  &.is-dragging {
    border-color: $--color-primary;
    background-color: $--color-primary-light-9;
  }

  /deep/ .el-loading-mask {
    border-radius: inherit;
  }

  @include breakpoint('sm-and-down') {
    margin-bottom: 0.5rem;
  }
}

.file-list {
  border-radius: inherit;
  overflow: auto;
  margin: 0;

  .sortable-ghost {
    visibility: hidden;
  }

  .sortable-drag {
    opacity: 1 !important;
  }

  .no-items,
  .error-message {
    display: flex;
    align-items: center;
    justify-content: center;
    color: $--color-gray-40;
    padding: 1rem;
    height: 100%;
    width: 100%;
  }

  .error-message {
    color: $--color-danger;
  }
}
</style>
